/* External dependencies */
import { filter, switchMap } from 'rxjs/operators';

/* Local dependencies */
import { getClient } from '../../../../clients/demirbank';
import { cleanPayload, removeSpaces } from '../../../common/helpers';
import { cleanService } from '../../utils/helper';
import { CreateServiceAction, CreateServiceActionTypes, createServiceFailed, createServiceSucceeded } from './actions';
import { createServiceMutation } from './mutation';

export default function createServiceEpic(action$) {
  return action$.pipe(
    filter((action: CreateServiceAction) => action.type === CreateServiceActionTypes.CREATE_SERVICE_REQUEST),
    switchMap((action: CreateServiceAction) =>
      createService(action)
        .then(createServiceSucceeded)
        .catch((error) => createServiceFailed(error)),
    ),
  );
}

export async function createService(action): Promise<Service> {
  const graphQLClient = await getClient();
  const { service } = action;

  const serviceInput = cleanPayload(service);

  const input = cleanService(removeSpaces(serviceInput));

  if (input?.logo?.url) {
    input.logo.url = input.logo.url.replace(/^data:image\/[a-z]+;base64,/, '');
  }

  if (input?.tags?.length > 0 && Array.isArray(input?.tags)) {
    const tags = new Set(input.tags.map((item) => item.id ?? item.value).filter(Boolean));

    input.tags = tags.size > 0 ? Array.from(tags) : undefined;
  } else {
    delete input?.tags;
  }

  if (input?.preferences?.length > 0 && Array.isArray(input?.preferences)) {
    input.preferences = input?.preferences.map((preference) => ({ fieldId: preference }));
  } else {
    delete input.preferences;
  }

  delete input.preference;

  const {
    rboCode,
    exactAmountRequired,
    id,
    logo,
    maxAmount,
    maxDigits,
    minAmount,
    minDigits,
    name_en,
    name_ky,
    name_ru,
    name_tr,
    placeholder_ru,
    placeholder_en,
    placeholder_ky,
    placeholder_tr,
    requiredFieldsServiceId,
    requiredFieldsProvider,
    preferences,
    serviceProviders,
    tags,
  } = input;

  const createServiceInput = {
    rboCode,
    exactAmountRequired,
    id,
    logo,
    maxAmount,
    maxDigits,
    minAmount,
    minDigits,
    name_en,
    name_ky,
    name_ru,
    name_tr,
    placeholder_ru,
    placeholder_en,
    placeholder_ky,
    placeholder_tr,
    requiredFieldsServiceId,
    requiredFieldsProvider,
    preferences,
    serviceProviders,
    tags,
  };

  const {
    data: { createService },
  } = await graphQLClient.mutate({
    mutation: createServiceMutation,
    variables: {
      input: createServiceInput,
    },
  });

  return createService as Service;
}
